import React, { useState, useEffect } from "react";

import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select } from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";
import QueueSelectSingle from "../QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
    buildPromptStackTemplate,
    hasStructuredPromptStack,
    validatePromptStack
} from "../../utils/promptStackTemplate";
import { buildMessageSuggestionPromptTemplate } from "../../utils/messageSuggestionPromptTemplate";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
    stackHelper: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1.5),
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.action.hover,
    },
    stackActions: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        [theme.breakpoints.down("sm")]: {
            alignItems: "flex-start",
            flexDirection: "column",
        },
    },
    stackStatus: {
        marginTop: theme.spacing(1),
    },
    stackError: {
        color: theme.palette.error.main,
    },
    stackWarning: {
        color: theme.palette.warning.dark,
    },
    stackSuccess: {
        color: theme.palette.success.main,
    },
}));

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(false);

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: "texto",
        voiceRegion: "",
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt(prevState => {
                    return { ...prevState, ...data };
                });
                setSelectedVoice(data.voice);
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    const handleClose = () => {
        setPrompt(initialState);
        setSelectedVoice("texto");
        onClose();
    };

    const handleChangeVoice = (e) => {
        setSelectedVoice(e.target.value);
    };

    const handleSavePrompt = async values => {
        const { voiceKey, ...promptDataValues } = values;
        const stackValidation = validatePromptStack(values.prompt);
        const promptData = {
            ...promptDataValues,
            voice: selectedVoice,
            maxMessages: values.maxMessages ?? 10,
        };
        if (!values.queueId) {
            toastError("Informe o setor");
            return;
        }
        if (stackValidation.isStructured && !stackValidation.isValid) {
            toastError(`Corrija o stack estruturado antes de salvar. ${stackValidation.errors[0] || ""}`.trim());
            return;
        }
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    const handleInsertStructuredTemplate = (values, setFieldValue) => {
        const currentPrompt = String(values.prompt || "");

        if (hasStructuredPromptStack(currentPrompt)) {
            toast.info("Este prompt ja parece usar o stack estruturado.");
            return;
        }

        setFieldValue("prompt", buildPromptStackTemplate(currentPrompt));
        toast.success("Template base do stack estruturado inserido. Revise os modos e as policies antes de salvar.");
    };

    const handleInsertMessageSuggestionTemplate = (values, setFieldValue) => {
        if (String(values.prompt || "").trim()) {
            toast.info("O campo de prompt já possui conteúdo. Apague-o antes de inserir o template de sugestão.");
            return;
        }

        setFieldValue("prompt", buildMessageSuggestionPromptTemplate());
        toast.success("Template de sugestão inserido. Personalize o tom e os exemplos antes de salvar.");
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    {promptId
                        ? `${i18n.t("promptModal.title.edit")}`
                        : `${i18n.t("promptModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => {
                        const stackValidation = validatePromptStack(values.prompt);

                        return (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.name")}
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                />
                                <FormControl fullWidth margin="dense" variant="outlined">
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.apikey")}
                                        name="apiKey"
                                        type={showApiKey ? 'text' : 'password'}
                                        error={touched.apiKey && Boolean(errors.apiKey)}
                                        helperText={touched.apiKey && errors.apiKey}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleToggleApiKey}>
                                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                                <div className={classes.stackHelper}>
                                    <div className={classes.stackActions}>
                                        <div>
                                            <Typography variant="subtitle2">
                                                Stack estruturado da Pink
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Insere um template base com CORE_PROMPT, MODE_RESOLVER_PROMPT, MODE_GUIDES e policies para editar neste prompt.
                                            </Typography>
                                        </div>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => handleInsertStructuredTemplate(values, setFieldValue)}
                                        >
                                            Inserir template base
                                        </Button>
                                    </div>
                                    <div className={classes.stackStatus}>
                                        {!stackValidation.isStructured && stackValidation.warnings.map((warning, index) => (
                                            <Typography key={`stack-warning-${index}`} variant="body2" className={classes.stackWarning}>
                                                {warning}
                                            </Typography>
                                        ))}
                                        {stackValidation.isStructured && stackValidation.isValid && (
                                            <Typography variant="body2" className={classes.stackSuccess}>
                                                Stack estruturado valido. Revise os modos e as policies antes de salvar em producao.
                                            </Typography>
                                        )}
                                        {stackValidation.errors.map((error, index) => (
                                            <Typography key={`stack-error-${index}`} variant="body2" className={classes.stackError}>
                                                {error}
                                            </Typography>
                                        ))}
                                        {stackValidation.isStructured && stackValidation.isValid && stackValidation.warnings.map((warning, index) => (
                                            <Typography key={`stack-structured-warning-${index}`} variant="body2" className={classes.stackWarning}>
                                                {warning}
                                            </Typography>
                                        ))}
                                    </div>
                                </div>
                                <div className={classes.stackHelper}>
                                    <div className={classes.stackActions}>
                                        <div>
                                            <Typography variant="subtitle2">
                                                Sugestão de respostas
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Insere uma orientação editável para corrigir e melhorar os rascunhos dos atendentes.
                                            </Typography>
                                        </div>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => handleInsertMessageSuggestionTemplate(values, setFieldValue)}
                                        >
                                            Inserir template de sugestão
                                        </Button>
                                    </div>
                                </div>
                                    <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.prompt")}
                                    name="prompt"
                                    error={touched.prompt && Boolean(errors.prompt)}
                                    helperText={touched.prompt && errors.prompt}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                    rows={10}
                                    multiline={true}
                                />
                                <QueueSelectSingle />
                                <div className={classes.multFieldLine}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                    <InputLabel>{i18n.t("promptModal.form.voice")}</InputLabel>
                                        <Select
                                            id="type-select"
                                            labelWidth={60}
                                            name="voice"
                                            value={selectedVoice}
                                            onChange={handleChangeVoice}
                                            multiple={false}
                                        >
                                            <MenuItem key={"texto"} value={"texto"}>
                                                Texto
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-FranciscaNeural"} value={"pt-BR-FranciscaNeural"}>
                                                Francisa
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-AntonioNeural"} value={"pt-BR-AntonioNeural"}>
                                                AntÃ´nio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-BrendaNeural"} value={"pt-BR-BrendaNeural"}>
                                                Brenda
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-DonatoNeural"} value={"pt-BR-DonatoNeural"}>
                                                Donato
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ElzaNeural"} value={"pt-BR-ElzaNeural"}>
                                                Elza
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-FabioNeural"} value={"pt-BR-FabioNeural"}>
                                                FÃ¡bio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-GiovannaNeural"} value={"pt-BR-GiovannaNeural"}>
                                                Giovanna
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-HumbertoNeural"} value={"pt-BR-HumbertoNeural"}>
                                                Humberto
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-JulioNeural"} value={"pt-BR-JulioNeural"}>
                                                Julio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-LeilaNeural"} value={"pt-BR-LeilaNeural"}>
                                                Leila
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-LeticiaNeural"} value={"pt-BR-LeticiaNeural"}>
                                                LetÃ­cia
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ManuelaNeural"} value={"pt-BR-ManuelaNeural"}>
                                                Manuela
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-NicolauNeural"} value={"pt-BR-NicolauNeural"}>
                                                Nicolau
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ValerioNeural"} value={"pt-BR-ValerioNeural"}>
                                                ValÃ©rio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-YaraNeural"} value={"pt-BR-YaraNeural"}>
                                                Yara
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.voiceRegion")}
                                        name="voiceRegion"
                                        error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                                        helperText={touched.voiceRegion && errors.voiceRegion}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                </div>
                                
                                <div className={classes.multFieldLine}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.temperature")}
                                        name="temperature"
                                        error={touched.temperature && Boolean(errors.temperature)}
                                        helperText={touched.temperature && errors.temperature}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.max_tokens")}
                                        name="maxTokens"
                                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                                        helperText={touched.maxTokens && errors.maxTokens}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}}
                </Formik>
            </Dialog>
        </div>
    );
};

export default PromptModal;





